import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SvgFragment from '../../../../fragments/SvgFragment';
import { styles } from '../../../../styles/themes/style';
import { ISenderIdentity } from '../../../../interfaces/models/campaign';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../../stores/application/useAppActions';

/**
 * A mailbox's deliverability and sending limit, in a dialog.
 *
 * Both used to sit inline on the sender card in the campaign form, where they took a
 * lot of vertical space in the middle of building a sequence — and neither is campaign
 * work. A domain's SPF/DKIM record and a mailbox's daily ceiling belong to the mailbox
 * and apply to every campaign it sends from, so they are configured once, here.
 *
 * The deliverability part is advisory and deliberately not a blocker: a domain missing
 * these still sends, it just lands in spam. Finding that out from a collapse in replies
 * is far worse than being told now.
 */

interface Props {
  show: boolean;
  identity: ISenderIdentity | null;
  onHide: () => void;
  /** The updated sender, so the caller can fold it back into its list. */
  onSaved?: (identity: ISenderIdentity) => void;
}

/** A mailbox is still ramping for its first couple of weeks. */
function isWarmingUp(warmupStartedAt: string | null): boolean {
  if (!warmupStartedAt) return false;
  const days = (Date.now() - new Date(warmupStartedAt).getTime()) / 86400000;
  return days < 18;
}

const MailboxSettingsDialog: React.FC<Props> = ({ show, identity, onHide, onSaved }) => {
  const { setMailDailyLimit } = useCampaignsRequest();
  const { setToast } = useAppActions();

  const [value, setValue] = useState('0');
  /**
   * 'rampup' is stored as an override of 0 — the schedule decides the number.
   *
   * Fixed is the default the dialog OPENS on when nothing has been chosen yet: it is
   * the mode someone comes here to use. A stored choice always wins over it.
   */
  const [mode, setMode] = useState<'fixed' | 'rampup'>('fixed');
  const [busy, setBusy] = useState(false);
  /*
    Failures go out as a toast, not as a line at the foot of the dialog.

    Down there it sat below the two mode cards, far from both the field that caused
    it and the button that triggered it — on a short window it was under the fold
    entirely, so Save appeared to do nothing at all.
  */

  const mail = identity?.mail;

  // Seed from the stored override each time the dialog opens, not on every render —
  // otherwise a refetch behind the dialog would overwrite what is being typed.
  useEffect(() => {
    if (!show || !mail) return;
    const override = mail.dailyLimitOverride || 0;
    /*
      A stored override means fixed; a stored 0 means the ramp. Only a mailbox that
      has never been configured falls through to the default above — otherwise this
      dialog would misreport what the mailbox is actually doing.
    */
    setMode(override > 0 ? 'fixed' : (mail.warmupStartedAt ? 'rampup' : 'fixed'));
    // The field starts from what is in force today, so switching to fixed does not
    // begin at a number nobody chose.
    setValue(String(override || mail.warmupSuggested || mail.dailyCap || 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, identity?._id]);

  if (!identity || !mail) return null;

  const save = async () => {
    // The ramp is stored as 0: no override, the schedule decides.
    const next = mode === 'rampup' ? 0 : parseInt(value, 10);
    if (mode === 'fixed' && (!Number.isFinite(next) || next < 1 || next > mail.hardCap)) {
      setToast({ type: 'error', message: `Enter a number between 1 and ${mail.hardCap}.` });
      return;
    }
    setBusy(true);

    const res: any = await setMailDailyLimit(identity._id, next);
    setBusy(false);

    const saved = res?.data?.identity;
    if (!saved) {
      setToast({ type: 'error', message: res?.message || 'Could not save that limit.' });
      return;
    }
    /*
      Closing IS the confirmation. The green line it replaces sat under a dialog the
      user then had to dismiss themselves, which read as if saving had not finished.
    */
    onSaved?.(saved);
    onHide();
  };

  const warnings = mail.domainCheck?.warnings ?? [];
  const typed = parseInt(value, 10);

  return (
    <StyledModal show={show} onHide={busy ? undefined : onHide} width="520px" backdrop>
      <Body>
        <Header>
          <div>
            {/* Named after the one thing it does, and after the menu item that opens
                it. "Mailbox settings" promised a settings page and delivered a single
                number — people went looking for the rest. */}
            <Title>Daily sending limit</Title>
            <Sub>{mail.address || identity.name || 'This sender'}</Sub>
          </div>
          <CloseBtn type="button" onClick={onHide} disabled={busy} aria-label="Close">
            <SvgFragment type="close" />
          </CloseBtn>
        </Header>

        {/*
          Only when there is something to say — a clean domain needs no panel.

          A free provider is the common case and gets its own headline, because the fix
          is different from a missing DNS record: nothing can be configured, the domain
          itself is the problem. SPF/DKIM badges are shown only for a real domain, where
          they are actionable.
        */}
        {warnings.length > 0 && (
          <Alert>
            <AlertTitle>
              {mail.domainCheck.freeProvider ? 'Personal email address' : 'Deliverability check'}
            </AlertTitle>
            {mail.domainCheck.freeProvider ? (
              // Says what happens, not what to do: there is nothing to fix from this
              // dialog, and "use a business domain" read as a step someone had missed.
              <AlertText>
                Cold emails from Gmail, Outlook and other personal addresses are filtered
                more often than mail from a company domain. This mailbox will still send —
                expect fewer messages to reach the inbox.
              </AlertText>
            ) : (
              <>
                {mail.domainCheck.domain && (
                  <Badges>
                    <Badge $ok={mail.domainCheck.spfOk}>SPF</Badge>
                    <Badge $ok={mail.domainCheck.dmarcOk}>DMARC</Badge>
                    <Badge $ok={mail.domainCheck.dkimOk}>DKIM</Badge>
                  </Badges>
                )}
                <List>
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </List>
              </>
            )}
          </Alert>
        )}

        {/*
          Two modes, chosen explicitly.

          The old form was a number field plus a "Use warm-up schedule" link, which
          never said which of the two you were on — 0 meant "follow the schedule", and
          nothing on screen admitted it. Radios make the current mode readable at a
          glance, and the number only belongs to one of them.
        */}
        <ModeCard $active={mode === 'fixed'} onClick={() => !busy && setMode('fixed')}>
          <ModeTop>
            <Radio type="radio" checked={mode === 'fixed'} readOnly />
            <div>
              <ModeTitle>Send the same number every day</ModeTitle>
              <ModeSub>This mailbox sends up to the number you set, and stays there until you change it.</ModeSub>
            </div>
          </ModeTop>

          {/* Always rendered, disabled when the other mode is chosen. Revealing it
              only on selection meant the number you were switching away from — and
              the one you would be switching back to — was invisible while deciding. */}
          <ModeBody>
            <Row>
              <NumInput
                type="number"
                min={1}
                max={mail.hardCap}
                value={value}
                disabled={busy || mode !== 'fixed'}
                onFocus={() => !busy && setMode('fixed')}
                onChange={e => setValue(e.target.value)}
              />
              <Unit>emails per day</Unit>
            </Row>
            {/*
              The ceiling is stated as a rule, and is contradicted the moment a bigger
              number is typed — `max` on a number input does not stop typing, so the
              field happily showed 300 under a line reading "Maximum 200" and said
              nothing until Save was pressed. Same message Save would give, said while
              the number is still being chosen.
            */}
            {mode === 'fixed' && Number.isFinite(typed) && typed > mail.hardCap ? (
              <ErrText>
                {mail.hardCap} a day is the most any mailbox may send here. Enter {mail.hardCap} or less.
              </ErrText>
            ) : (
              <Hint>Up to {mail.hardCap} a day — the most any one mailbox may send.</Hint>
            )}

              {/* Warn, don't block: it's their domain, but a young mailbox pushed hard
                  is the classic way to get filtered permanently. */}
            {mode === 'fixed' && typed <= mail.hardCap && isWarmingUp(mail.warmupStartedAt) && typed > mail.warmupSuggested && (
              <WarnText>
                This mailbox is still warming up — the schedule suggests {mail.warmupSuggested}/day.
                Going higher this early raises the odds of landing in spam.
              </WarnText>
            )}
          </ModeBody>
        </ModeCard>

        <ModeCard $active={mode === 'rampup'} onClick={() => !busy && setMode('rampup')}>
          <ModeTop>
            <Radio type="radio" checked={mode === 'rampup'} readOnly />
            <div>
              {/* "Warm-up" is the industry word, and it is the one every other tool
                  uses — but it is explained here rather than assumed, because it is the
                  only place in the product where it appears. */}
              <ModeTitle>Start low and build up (warm-up)</ModeTitle>
              <ModeSub>
                Raise the limit a little each day on its own. A new mailbox that sends a lot
                straight away is the quickest way to end up in spam folders for good.
              </ModeSub>
            </div>
          </ModeTop>

          {mode === 'rampup' && (
            <ModeBody>
              {/*
                The schedule's own numbers, read from the server rather than written
                here — the ramp is configurable by env, so a figure typed into the UI
                would be a guess that drifts.

                "Follows the warm-up schedule" told nobody what they would actually be
                allowed to send today, which is the only number that matters right now.
              */}
              <RampLine>
                <strong>Today this mailbox may send {mail.warmupSuggested} emails</strong>
                {!!mail.warmupStart && !!mail.warmupIncrement && (
                  <RampDetail>
                    Began at {mail.warmupStart} a day, and goes up by {mail.warmupIncrement} every day
                    until it reaches {mail.hardCap}.
                  </RampDetail>
                )}
              </RampLine>
            </ModeBody>
          )}
        </ModeCard>


        <Footer>
          <SecondaryBtn type="button" onClick={onHide} disabled={busy}>Cancel</SecondaryBtn>
          <PrimaryBtn type="button" disabled={busy} onClick={() => save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </PrimaryBtn>
        </Footer>
      </Body>
    </StyledModal>
  );
};

export default MailboxSettingsDialog;

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Body = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  padding: 20px 22px 18px;
`;

const Header = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
`;

const Title = styled.div`
  font-family: NunitoSans; font-size: 17px; font-weight: 800; color: #0f172a;
`;

const Sub = styled.div`
  margin-top: 2px;
  font-family: NunitoSans; font-size: 12px; color: #94a3b8;
`;

const CloseBtn = styled.button`
  flex: 0 0 auto;
  background: none; border: none; padding: 2px; cursor: pointer; line-height: 0;
  .svg { width: 18px; height: 18px; }
  &:disabled { opacity: .4; cursor: default; }
`;

const Alert = styled.div`
  border: 1px solid #fcd34d; background: #fffbeb; border-radius: 10px;
  padding: 11px 13px;
  font-family: NunitoSans; font-size: 12px; line-height: 1.55; color: #92400e;
`;

const AlertTitle = styled.div`
  font-weight: 800; margin-bottom: 4px;
`;

const Badges = styled.div`
  display: flex; gap: 6px; margin: 6px 0 2px;
`;

const Badge = styled.span<{ $ok?: boolean }>`
  padding: 2px 8px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.04em;
  background: ${p => (p.$ok ? '#ecfdf5' : '#fef2f2')};
  color: ${p => (p.$ok ? '#047857' : '#b91c1c')};
`;

const List = styled.ul`
  margin: 6px 0 0; padding-left: 16px;
`;

const ModeCard = styled.div<{ $active?: boolean }>`
  border: 1.5px solid ${p => (p.$active ? ACCENT : "#e6eaf1")};
  background: ${p => (p.$active ? "#fbfcff" : "#fff")};
  border-radius: 10px; padding: 12px 14px; cursor: pointer;
  display: flex; flex-direction: column; gap: 10px;
  transition: border-color 0.12s, background 0.12s;
`;

const ModeTop = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
`;

const Radio = styled.input`
  margin-top: 2px; width: 15px; height: 15px; flex-shrink: 0;
  accent-color: ${ACCENT}; cursor: pointer;
`;

const ModeTitle = styled.div`
  font-family: NunitoSans; font-size: 13px; font-weight: 800; color: #0f172a;
`;

const ModeSub = styled.div`
  margin-top: 2px;
  font-family: NunitoSans; font-size: 11.5px; line-height: 1.5; color: #64748b;
`;

/* Indented to the title, so the controls read as belonging to the mode above them. */
const ModeBody = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding-left: 25px;
`;

const Unit = styled.span`
  font-family: NunitoSans; font-size: 12px; color: #64748b;
`;



const Row = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;

const NumInput = styled.input`
  width: 100px; box-sizing: border-box; height: 36px;
  border: 1.5px solid #dde3ee; border-radius: 8px; padding: 0 10px; background: #fff;
  font-family: NunitoSans; font-size: 13px; color: #0f172a;
  &:focus { outline: none; border-color: ${ACCENT}; }
  &:disabled { background: #f7f9fc; }
`;

const PrimaryBtn = styled.button`
  border: none; border-radius: 8px; padding: 9px 16px;
  background: ${ACCENT}; color: #fff; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  &:disabled { opacity: .5; cursor: default; }
`;

const SecondaryBtn = styled.button`
  border: 1.5px solid #dde3ee; border-radius: 8px; padding: 9px 16px;
  background: #fff; color: #475569; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  &:hover:not(:disabled) { border-color: ${ACCENT}; color: ${ACCENT}; }
  &:disabled { opacity: .5; cursor: default; }
`;


const AlertText = styled.div`
  margin-top: 2px;
`;

const RampLine = styled.div`
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  font-family: NunitoSans; font-size: 12px; color: #0f172a;
`;

const RampDetail = styled.span`
  font-size: 11px; font-weight: 600; color: #94a3b8;
`;

const Hint = styled.div`
  font-family: NunitoSans; font-size: 11px; line-height: 1.5; color: #94a3b8;
`;

const WarnText = styled.div`
  font-family: NunitoSans; font-size: 11px; line-height: 1.5; color: #b45309;
`;

const ErrText = styled.div`
  font-family: NunitoSans; font-size: 11px; line-height: 1.5; color: #dc2626;
`;


const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
`;
